import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Area } from "@/lib/areas";

// Espejo de la validación de servidor (factory/equipo_dto.go): nombre obligatorio ≤120,
// descripción ≤500, área obligatoria, criticidad ∈ {ALTA,MEDIA,BAJA}. id_area se coacciona
// a número porque el <select> nativo entrega string.
const equipoSchema = z.object({
  nombre_equipo: z
    .string()
    .trim()
    .min(1, "El nombre es obligatorio")
    .max(120, "Máximo 120 caracteres"),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
  id_area: z.coerce.number().int().positive("Selecciona un área"),
  criticidad: z.enum(["ALTA", "MEDIA", "BAJA"]),
  loteable: z.boolean(),
  activo: z.boolean(),
});

// El esquema coacciona id_area (z.coerce.number), de modo que el tipo de entrada del
// formulario (lo que teclea el usuario / los valores por defecto) difiere del de salida
// ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type EquipoFormInput = z.input<typeof equipoSchema>;
export type EquipoFormValues = z.output<typeof equipoSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<EquipoFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of ["nombre_equipo", "descripcion", "id_area", "criticidad"] as const) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar el equipo.");
    return;
  }
  setFormError("No se pudo guardar el equipo. Comprueba tu conexión.");
}

/**
 * EquipoForm es el formulario reutilizable de alta/edición de equipos (react-hook-form +
 * zod). En edición muestra el código EQ-XXXX de solo lectura (lo asigna el servidor y es
 * inmutable). Mapea los errores por campo del backend (VALIDACION). Sigue el patrón de
 * AreaForm (#27).
 */
export function EquipoForm({
  defaultValues,
  areas,
  codigo,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: EquipoFormValues;
  areas: Area[];
  codigo?: string;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: EquipoFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EquipoFormInput, unknown, EquipoFormValues>({
    resolver: zodResolver(equipoSchema),
    defaultValues,
  });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      {codigo ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="codigo_equipo">Código</Label>
          <Input id="codigo_equipo" value={codigo} disabled readOnly />
          <p className="text-slate-500 text-sm">El código lo asigna el sistema y no cambia.</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="nombre_equipo">Nombre</Label>
        <Input
          id="nombre_equipo"
          autoComplete="off"
          aria-invalid={errors.nombre_equipo ? true : undefined}
          {...register("nombre_equipo")}
        />
        <FieldError>{errors.nombre_equipo?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_area">Área</Label>
        <Select
          id="id_area"
          aria-invalid={errors.id_area ? true : undefined}
          {...register("id_area")}
        >
          <option value="0" disabled>
            Selecciona un área
          </option>
          {areas.map((area) => (
            <option key={area.id} value={area.id}>
              {area.nombre}
            </option>
          ))}
        </Select>
        <FieldError>{errors.id_area?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="criticidad">Criticidad</Label>
        <Select
          id="criticidad"
          aria-invalid={errors.criticidad ? true : undefined}
          {...register("criticidad")}
        >
          <option value="ALTA">Alta</option>
          <option value="MEDIA">Media</option>
          <option value="BAJA">Baja</option>
        </Select>
        <FieldError>{errors.criticidad?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("loteable")}
        />
        <span className="text-base text-slate-800">Loteable (admite LOTO)</span>
      </label>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activo")}
        />
        <span className="text-base text-slate-800">Activo</span>
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
